import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

interface SiteHeaderProps {
  coupleNames: string;
  coupleNamesZh?: string;
  homeHref: string;
  logoUrl?: string;
}

const MOBILE_BAR_HEIGHT = 'calc(50px + env(safe-area-inset-top, 0px))';

export default function SiteHeader({ coupleNames, coupleNamesZh, homeHref, logoUrl }: SiteHeaderProps) {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const display = (i18n.language === 'zh_hk' && coupleNamesZh) ? coupleNamesZh : coupleNames;
  const barRef = useRef<HTMLElement>(null);
  const [barHeight, setBarHeight] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const visible = Boolean(logoUrl || display);

  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const apply = () => {
      const next = el.offsetHeight;
      setBarHeight((prev) => (prev === next ? prev : next));
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible, logoUrl, display]);

  useEffect(() => {
    if (!visible) return;
    const root = document.getElementById('root');
    const scroller: HTMLElement | Window = phone && root ? root : window;
    const read = () => {
      const top = scroller === window ? window.scrollY : (scroller as HTMLElement).scrollTop;
      const next = top > 0;
      setScrolled((prev) => (prev === next ? prev : next));
    };
    read();
    scroller.addEventListener('scroll', read, { passive: true });
    return () => scroller.removeEventListener('scroll', read);
  }, [phone, visible]);

  if (!visible) return <LanguageSwitcher />;

  return (
    <>
      <LanguageSwitcher />
      <Box
        component="a"
        href={homeHref}
        ref={barRef}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: (muiTheme) => muiTheme.zIndex.appBar,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
          bgcolor: 'background.default',
          color: 'primary.dark',
          textDecoration: 'none',
          minHeight: { xs: MOBILE_BAR_HEIGHT, sm: 'auto' },
          pt: {
            xs: 'env(safe-area-inset-top, 0px)',
            sm: 'calc(env(safe-area-inset-top, 0px) + 16px)',
          },
          pb: { xs: 0, sm: 2 },
          px: { xs: 7, sm: 8 },
          '&:hover': { opacity: 0.8 },
        }}
      >
        {logoUrl ? (
          <Box
            component="img"
            src={logoUrl}
            alt={display || 'Wedding logo'}
            sx={{
              display: 'block',
              alignSelf: 'center',
              maxHeight: { xs: 40, sm: 132 },
              height: 'auto',
              width: 'auto',
              maxWidth: '100%',
              minWidth: 0,
            }}
          />
        ) : (
          <Typography
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontSize: { xs: 18, sm: 40 },
              fontWeight: 700,
              letterSpacing: { xs: 1.2, sm: 2.2 },
              color: 'primary.dark',
              textAlign: 'center',
              lineHeight: 1.1,
              maxWidth: '100%',
              whiteSpace: { xs: 'nowrap', sm: 'normal' },
              overflow: { xs: 'hidden', sm: 'visible' },
              textOverflow: { xs: 'ellipsis', sm: 'unset' },
            }}
          >
            {display}
          </Typography>
        )}
        <Box
          aria-hidden
          sx={(muiTheme) => ({
            position: 'absolute',
            left: 0,
            right: 0,
            top: '100%',
            height: 24,
            background: `linear-gradient(to bottom, ${muiTheme.palette.background.default}, transparent)`,
            opacity: scrolled ? 1 : 0,
            transition: 'opacity 200ms ease',
            pointerEvents: 'none',
          })}
        />
      </Box>
      <Box sx={{ height: { xs: MOBILE_BAR_HEIGHT, sm: barHeight ? `${barHeight}px` : 0 } }} />
    </>
  );
}
